import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import { auditApi } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { LoadingState, Panel, SkeletonRows } from '../../components/ui/index.js';
import { formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';

/**
 * Who did what to this request, and when.
 *
 * ===========================================================================
 * READ FROM THE AUDIT TRAIL, WHICH IS THE RECORD OF RESPONSIBILITY ITSELF.
 * ===========================================================================
 *
 * Every important act on a work order already writes an audit entry naming the
 * user and the moment (CLAUDE.md section 5.5), and that history can never be
 * edited to hide a change. Rebuilding a second "activity" store beside it
 * would produce a record that could disagree with the one that matters, so
 * this panel reads the real one.
 *
 * READING IT NEEDS `audit.view`, AND WHERE SOMEBODY DOES NOT HAVE IT THIS
 * PANEL SAYS SO. It does not render an empty list. An empty list would assert
 * that nothing has happened to this request, which is a different statement
 * from "you may not read who acted on it" — and the wrong one
 * (CLAUDE.md section 8).
 */
export function WorkOrderHistory({
  workOrderId,
  timezone,
}: {
  readonly workOrderId: string;
  readonly timezone?: string;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const locale = currentLocale();

  const mayRead = can(PERMISSIONS.AUDIT_VIEW);

  const entries = useQuery({
    queryKey: ['audit', 'work-order', workOrderId],
    queryFn: () =>
      auditApi.list({
        entityType: 'work_order',
        entityId: workOrderId,
        pageSize: 50,
      }),
    enabled: mayRead,
  });

  if (!mayRead) {
    return (
      <Panel title={t('workOrders.historyTitle')}>
        {/* Not an empty list: a statement about permission, not about events. */}
        <p className="text-body text-default-600">
          {t('workOrders.historyRestricted')}
        </p>
      </Panel>
    );
  }

  return (
    <Panel
      title={t('workOrders.historyTitle')}
      description={t('workOrders.historyHint')}
    >
      {entries.isLoading ? (
        <>
          <LoadingState label={t('common.loading')} />
          <SkeletonRows rows={3} />
        </>
      ) : entries.error ? (
        // A failure to READ the history is reported as a failure. It is not
        // allowed to look like a request with no history.
        <p className="text-body text-warning-600">
          {t('workOrders.historyUnavailable')}
        </p>
      ) : (entries.data?.data ?? []).length === 0 ? (
        <p className="text-body text-default-600">
          {t('workOrders.historyNone')}
        </p>
      ) : (
        // A timeline (D278): a hairline down the start edge, a dot per event.
        <ol className="flex flex-col gap-3 border-s border-default-200 ps-4">
          {(entries.data?.data ?? []).map((entry) => (
            <li key={entry.id} className="relative min-w-0">
              <span
                aria-hidden="true"
                className="absolute -start-[21px] top-1.5 size-2 rounded-full bg-default-400"
              />
              <p className="text-body font-medium text-default-900">
                {t(`audit.actions.${entry.action}`, {
                  defaultValue: entry.action,
                })}
              </p>
              <p className="text-caption text-default-600">
                {/* The responsible person, named. `system` where no user acted. */}
                {entry.actor?.displayName ?? t('audit.system')}
                {entry.actor?.role
                  ? ` — ${t(`roles.${entry.actor.role}`, {
                      defaultValue: entry.actor.role,
                    })}`
                  : ''}
              </p>
              <p className="text-caption text-default-600">
                {formatDateTime(entry.occurredAt, {
                  locale,
                  ...(timezone ? { timezone } : {}),
                })}
              </p>
              {entry.reason ? (
                <p className="text-caption text-default-700">{entry.reason}</p>
              ) : null}
            </li>
          ))}
        </ol>
      )}

      <p className="mt-stack text-caption text-default-600">
        {t('audit.immutableNotice')}
      </p>
    </Panel>
  );
}
